import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import { get, post } from "../utils/apiManager.js";
import { baseURL } from "../config/config.js";
import { toast } from "react-toastify";
import { LoginDetails } from "../reduxToolkit/slice/auth.js";
import TwoFactorSettings from "../components/TwoFactorSettings.js";

const EyeIcon = ({ show }) => (
  <span style={{ fontSize: 16, lineHeight: 1 }}>
    {show ? "🙈" : "👁️"}
  </span>
);

const Profile = () => {
  const { profileImage, userId } = useSelector(state => state.auth);
  const token = localStorage.getItem("token");
  const dispatch = useDispatch();
  const inputRef = useRef();

  const [isShowEditProfile, setIsShowEditProfile] = useState(true);
  const [profileName, setProfileName] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState(profileImage);
  const [sendProfileImage, setSendProfileImage] = useState({});
  const [isProfileUpdate, setIsProfileUpdate] = useState(false);
  const [isChangePassword, setIsChangePassword] = useState(true);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => { setAvatarFailed(false); }, [profileImageUrl]);

  useEffect(() => { getOneProfile(); }, []);

  const getOneProfile = () => {
    // The login is sent in the request header, not in the address (addresses end up in server logs)
    get(`/admin/profile`).then((res) => {
      setProfileName(res?.data?.name);
      setProfileEmail(res?.data?.email);
      if (res?.data?.profileImage) {
        setProfileImageUrl(`${baseURL}/${res?.data?.profileImage}`);
        setIsProfileUpdate(false);
      }
    }).catch((err) => console.log(err));
  };

  const profileUpdateHandler = () => {
    const file = inputRef?.current?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImageUrl(`data:image/png;base64,${reader.result.split(',')[1]}`);
        setSendProfileImage(file);
        setIsProfileUpdate(true);
        setIsShowEditProfile(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const updateProfile = () => {
    setSavingProfile(true);
    const formData = new FormData();
    formData.append("name", profileName);
    formData.append("id", userId);
    if (isProfileUpdate) formData.append("image", sendProfileImage);
    formData.append("isProfileUpdate", isProfileUpdate);
    post(`${baseURL}/admin/profile/edit`, formData, 1).then((res) => {
      setSavingProfile(false);
      if (res?.status) {
        dispatch(LoginDetails({ name: profileName, isLogin: true, userId, token, profileImage }));
        toast.success(res?.message);
        setIsShowEditProfile(true);
      } else toast.error(res?.message);
    }).catch((err) => { setSavingProfile(false); toast.error(err?.response?.data?.error); });
  };

  const closeEmailForm = () => {
    setShowEmailForm(false); setNewEmail(""); setConfirmEmail(""); setEmailPassword("");
  };

  const changeEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Please enter a valid email address");
    if (email !== confirmEmail.trim().toLowerCase()) return toast.error("The two email addresses do not match");
    if (!emailPassword) return toast.error("Enter your current password");
    setSavingEmail(true);
    post(`${baseURL}/admin/change-email`, { newEmail: email, currentPassword: emailPassword }).then((res) => {
      setSavingEmail(false);
      if (res?.status) {
        toast.success(res?.message);
        setProfileEmail(res?.data?.email || email);
        closeEmailForm();
      } else toast.error(res?.message);
    }).catch((err) => { setSavingEmail(false); toast.error(err?.response?.data?.message || "Could not change email"); });
  };

  const ChangePassword = () => {
    if (!oldPassword) return toast.error("Current password is required");
    if (newPassword.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPassword !== confirmPassword) return toast.error("New password and confirm password do not match");
    setSavingPassword(true);
    post(`${baseURL}/admin/change-password`, { oldPassword, newPassword, id: userId }).then((res) => {
      setSavingPassword(false);
      if (res?.status) {
        // Changing the password signs out every other session; keep this one signed in
        if (res?.token) dispatch(LoginDetails({ name: profileName, isLogin: true, userId, token: res.token, profileImage }));
        toast.success(res?.message);
        setIsChangePassword(true);
        setOldPassword(""); setNewPassword(""); setConfirmPassword("");
      } else toast.error(res?.message);
    }).catch((err) => { setSavingPassword(false); toast.error(err?.response?.data?.error); });
  };

  const inputS = (disabled) => ({
    width: '100%', padding: '10px 40px 10px 14px', borderRadius: 8,
    border: '1.5px solid #e2e8f0', fontSize: 14, color: disabled ? '#9aa5c0' : '#0a1628',
    background: disabled ? '#f8f9fc' : '#fff', outline: 'none',
    boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color .15s',
  });

  const btnStyle = (primary, disabled) => ({
    padding: '10px 22px', borderRadius: 8, border: 'none', fontFamily: 'inherit',
    fontSize: 14, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? '#e2e8f0' : primary ? '#AD2429' : '#1a2744',
    color: disabled ? '#9aa5c0' : '#fff',
    opacity: disabled ? 0.7 : 1,
    transition: 'all .15s',
  });

  const initials = profileName ? profileName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'AD';

  return (
    <div>

      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0a1628', margin: '0 0 4px', letterSpacing: '-0.5px' }}>My Profile</h2>
        <p style={{ fontSize: 13, color: '#9aa5c0', margin: 0 }}>Manage your account information and password</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* ── MY ACCOUNT ── */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #edf0f7', boxShadow: '0 1px 4px rgba(26,39,68,.05)', overflow: 'hidden' }}>
          <div style={{ padding: '18px 24px', borderBottom: '1px solid #edf0f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0a1628', margin: 0 }}>My Account</h3>
              <p style={{ fontSize: 13, color: '#9aa5c0', margin: '2px 0 0' }}>Your name, photo and login email</p>
            </div>
            {isShowEditProfile ? (
              <button onClick={() => setIsShowEditProfile(false)} style={btnStyle(false, false)}>Edit Profile</button>
            ) : (
              <button onClick={() => setIsShowEditProfile(true)} style={{ ...btnStyle(false, false), background: '#f0f2f8', color: '#5a6a8a' }}>Cancel</button>
            )}
          </div>

          <div style={{ padding: '24px' }}>
            {/* Avatar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid #edf0f7' }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', border: '3px solid #edf0f7', background: '#AD2429', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {profileImageUrl && !avatarFailed
                    ? <img src={profileImageUrl} alt="" onError={() => setAvatarFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ color: '#fff', fontSize: 22, fontWeight: 700 }}>{initials}</span>
                  }
                </div>
                {!isShowEditProfile && (
                  <button
                    onClick={() => inputRef.current.click()}
                    style={{ position: 'absolute', bottom: 0, right: 0, width: 26, height: 26, borderRadius: '50%', background: '#1a2744', border: '2px solid #fff', color: '#fff', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    ✎
                  </button>
                )}
                <input type="file" hidden ref={inputRef} onChange={profileUpdateHandler} accept="image/*" />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0a1628', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profileName || 'Admin'}</div>
                <div style={{ fontSize: 13, color: '#9aa5c0', marginTop: 2 }}>Super Administrator</div>
                {!isShowEditProfile && <div style={{ fontSize: 12, color: '#AD2429', marginTop: 4 }}>Click avatar to change photo</div>}
              </div>
            </div>

            {/* Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'block' }}>Name</label>
                <input
                  placeholder="Your name"
                  type="text"
                  maxLength={30}
                  value={profileName || ''}
                  onChange={(e) => setProfileName(e.target.value.slice(0, 30))}
                  disabled={isShowEditProfile}
                  style={{ ...inputS(isShowEditProfile), paddingRight: 14 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'block' }}>Email address</label>
                <input
                  placeholder="Email"
                  type="email"
                  value={profileEmail || ''}
                  disabled
                  style={{ ...inputS(true), paddingRight: 14 }}
                />
                {!showEmailForm && (
                  <button type="button" onClick={() => setShowEmailForm(true)}
                    style={{ background: 'none', border: 'none', padding: 0, marginTop: 6, color: '#AD2429', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Change email
                  </button>
                )}
              </div>
            </div>

            {showEmailForm && (
              <div style={{ border: '1px solid #edf0f7', background: '#fafbfd', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0a1628', marginBottom: 4 }}>Change login email</div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
                  You'll log in with the new address, and password reset codes will be sent there. Both addresses get a notice.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'block' }}>New email</label>
                    <input type="email" autoComplete="off" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="you@example.com" style={{ ...inputS(false), paddingRight: 14 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'block' }}>Type it again</label>
                    <input type="email" autoComplete="off" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder="you@example.com"
                      style={{ ...inputS(false), paddingRight: 14, borderColor: confirmEmail && confirmEmail.trim().toLowerCase() !== newEmail.trim().toLowerCase() ? '#AD2429' : '#e2e8f0' }} />
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'block' }}>Current password</label>
                  <input type="password" autoComplete="current-password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} placeholder="Confirm it's you" style={{ ...inputS(false), paddingRight: 14 }} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={changeEmail} disabled={savingEmail} style={btnStyle(true, savingEmail)}>{savingEmail ? 'Saving...' : 'Save new email'}</button>
                  <button type="button" onClick={closeEmailForm} style={{ ...btnStyle(false, false), background: '#f0f2f8', color: '#5a6a8a' }}>Cancel</button>
                </div>
              </div>
            )}

            {!isShowEditProfile && (
              <button onClick={updateProfile} disabled={savingProfile} style={btnStyle(true, savingProfile)}>
                {savingProfile ? 'Saving...' : 'Update Profile'}
              </button>
            )}
          </div>
        </div>

        {/* ── CHANGE PASSWORD ── */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #edf0f7', boxShadow: '0 1px 4px rgba(26,39,68,.05)', overflow: 'hidden' }}>
          <div style={{ padding: '18px 24px', borderBottom: '1px solid #edf0f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0a1628', margin: 0 }}>Change Password</h3>
              <p style={{ fontSize: 13, color: '#9aa5c0', margin: '2px 0 0' }}>Update your account password</p>
            </div>
            {isChangePassword ? (
              <button onClick={() => setIsChangePassword(false)} style={btnStyle(false, false)}>Change Password</button>
            ) : (
              <button onClick={() => { setIsChangePassword(true); setOldPassword(''); setNewPassword(''); setConfirmPassword(''); }} style={{ ...btnStyle(false, false), background: '#f0f2f8', color: '#5a6a8a' }}>Cancel</button>
            )}
          </div>

          <div style={{ padding: '24px' }}>
            {isChangePassword ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 0', gap: 10 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: '#f0f2f8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🔒</div>
                <p style={{ fontSize: 14, color: '#9aa5c0', margin: 0, textAlign: 'center' }}>Click "Change Password" to update<br />your account password</p>
                <p style={{ fontSize: 13, color: '#9aa5c0', margin: '8px 0 0', textAlign: 'center', maxWidth: 340 }}>
                  Forgot your current password? Sign out and use "Forgot password?" on the login page. The code is emailed to {profileEmail || 'your login email'}.
                </p>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'block' }}>Current Password</label>
                    <div style={{ position: 'relative' }}>
                      <input placeholder="Enter current password" type={showOldPassword ? "text" : "password"} value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} style={inputS(false)} />
                      <button
                        type="button"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        style={{
                          position: 'absolute',
                          right: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <EyeIcon show={showOldPassword} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'block' }}>New Password</label>
                    <div style={{ position: 'relative' }}>
                      <input placeholder="At least 8 characters" type={showNewPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={inputS(false)} />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{
                          position: 'absolute',
                          right: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <EyeIcon show={showNewPassword} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'block' }}>Confirm Password</label>
                    <div style={{ position: 'relative' }}>
                      <input placeholder="Confirm new password" type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                        style={{ ...inputS(false), borderColor: confirmPassword && confirmPassword !== newPassword ? '#AD2429' : '#e2e8f0' }} />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: 'absolute',
                          right: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <EyeIcon show={showConfirmPassword} />
                      </button>
                    </div>
                    {confirmPassword && confirmPassword !== newPassword && (
                      <span style={{ fontSize: 12, color: '#AD2429', marginTop: 4, display: 'block' }}>Passwords do not match</span>
                    )}
                  </div>
                </div>
                <button onClick={ChangePassword} disabled={savingPassword || (confirmPassword && confirmPassword !== newPassword)} style={btnStyle(true, savingPassword || (confirmPassword && confirmPassword !== newPassword))}>
                  {savingPassword ? 'Updating...' : 'Update Password'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── TWO-STEP SIGN-IN ── */}
        <TwoFactorSettings />
      </div>
    </div>
  );
};

export default Profile;
